import {Component, inject} from '@angular/core';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {Roles} from '@core/enums/Roles';
import {Account, AdminApiService, ClientCompany, apiErrorMessage} from '@services/api/admin/admin-api.service';

export interface CompanyDialogResult {
  company: ClientCompany;
  admin?: Account;
}

/** Nuova azienda cliente (con il suo primo amministratore) o modifica dei dati di un'azienda. */
@Component({
  selector: 'app-company-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, TranslateModule],
  template: `
    <h2 mat-dialog-title>{{ (editing ? 'admin.edit_company' : 'admin.new_company') | translate }}</h2>
    <form [formGroup]="form" (ngSubmit)="save()">
      <mat-dialog-content>
        @if (error) {
          <p class="dm-a-error" role="alert">{{ error }}</p>
        }
        <p class="dm-a-section">{{ 'admin.company_data' | translate }}</p>
        <div class="dm-a-dialog-grid" formGroupName="company">
          <mat-form-field appearance="outline" class="full">
            <mat-label>{{ 'admin.company_name' | translate }}</mat-label>
            <input matInput formControlName="name"/>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{ 'admin.vat_number' | translate }}</mat-label>
            <input matInput formControlName="vat_number"/>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{ 'admin.fiscal_code' | translate }}</mat-label>
            <input matInput formControlName="fiscal_code"/>
          </mat-form-field>
          <mat-form-field appearance="outline" class="full">
            <mat-label>{{ 'admin.company_email' | translate }}</mat-label>
            <input matInput type="email" formControlName="email"/>
          </mat-form-field>
          <mat-form-field appearance="outline" class="full">
            <mat-label>{{ 'admin.address' | translate }}</mat-label>
            <input matInput formControlName="registered_office_address"/>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{ 'admin.city' | translate }}</mat-label>
            <input matInput formControlName="registered_office_city"/>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{ 'admin.province' | translate }}</mat-label>
            <input matInput formControlName="registered_office_province"/>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{ 'admin.cap' | translate }}</mat-label>
            <input matInput formControlName="registered_office_cap"/>
          </mat-form-field>
        </div>

        @if (!editing) {
          <p class="dm-a-section">{{ 'admin.first_admin' | translate }}</p>
          <div class="dm-a-dialog-grid" formGroupName="admin">
            <mat-form-field appearance="outline">
              <mat-label>{{ 'admin.name' | translate }}</mat-label>
              <input matInput formControlName="name"/>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>{{ 'admin.lastname' | translate }}</mat-label>
              <input matInput formControlName="lastname"/>
            </mat-form-field>
            <mat-form-field appearance="outline" class="full">
              <mat-label>{{ 'admin.email' | translate }}</mat-label>
              <input matInput type="email" formControlName="email"/>
            </mat-form-field>
          </div>
          <p class="dm-a-hint">{{ 'admin.first_admin_hint' | translate }}</p>
        }
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button type="button" class="dm-a-btn ghost" mat-dialog-close>{{ 'admin.cancel' | translate }}</button>
        <button type="submit" class="dm-a-btn primary" [disabled]="form.invalid || saving">
          {{ (editing ? 'admin.save' : 'admin.create_company') | translate }}
        </button>
      </mat-dialog-actions>
    </form>
  `,
})
export class CompanyDialogComponent {
  private existing: ClientCompany | undefined = inject(MAT_DIALOG_DATA, {optional: true}) ?? undefined;
  private dialogRef = inject(MatDialogRef<CompanyDialogComponent, CompanyDialogResult>);
  private api = inject(AdminApiService);
  private translate = inject(TranslateService);
  private fb = inject(FormBuilder);

  readonly editing = !!this.existing;
  saving = false;
  error = '';
  /** Se l'amministratore non va a buon fine, l'azienda e' gia' creata: un nuovo invio la aggiorna. */
  private created: ClientCompany | undefined;

  form = this.fb.nonNullable.group({
    company: this.fb.nonNullable.group({
      name: [this.existing?.name ?? '', Validators.required],
      vat_number: [this.existing?.vat_number ?? '', Validators.required],
      fiscal_code: [this.existing?.fiscal_code ?? '', Validators.required],
      email: [this.existing?.email ?? '', [Validators.required, Validators.email]],
      registered_office_address: [this.existing?.registered_office_address ?? '', Validators.required],
      registered_office_city: [this.existing?.registered_office_city ?? '', Validators.required],
      registered_office_province: [this.existing?.registered_office_province ?? '', Validators.required],
      registered_office_cap: [this.existing?.registered_office_cap ?? '', Validators.required],
    }),
    admin: this.fb.nonNullable.group({
      name: ['', this.existing ? [] : Validators.required],
      lastname: [''],
      email: ['', this.existing ? [] : [Validators.required, Validators.email]],
    }),
  });

  save(): void {
    if (this.form.invalid) {
      return;
    }
    this.saving = true;
    this.error = '';
    const {company, admin} = this.form.getRawValue();
    const target = this.existing ?? this.created;
    const saveCompany = target ? this.api.updateCompany(target.id, company) : this.api.createCompany(company);

    saveCompany.subscribe({
      next: (saved) => {
        if (this.editing) {
          this.dialogRef.close({company: saved});
          return;
        }
        this.created = saved;
        this.api.createAccount({...admin, email: admin.email.trim(), role: Roles.MANAGEMENT_SYSTEM, company: saved.id}).subscribe({
          next: (account) => this.dialogRef.close({company: saved, admin: account}),
          error: (err) => this.fail(err),
        });
      },
      error: (err) => this.fail(err),
    });
  }

  private fail(err: unknown): void {
    this.saving = false;
    this.error = apiErrorMessage(err, this.translate.instant('admin.error_generic'));
  }
}
